import { ChunkDataProvider } from '../api';
import { CommonRegistry } from '../components/components';
import { Rect } from '../geometryUtils';
import { calculateCornerCorrection, checkMapCollision } from '../mapCollisionUtils';
import {
  CommonFilteredComponentSystem,
  FilteredComponentSystemUpdateEntityArgs,
} from './SystemBase';

/**
 * Collision system that checks entity colliders against map collision shapes.
 *
 * Subclasses must implement getChunkDataProvider() to return the appropriate
 * service for their environment (server or client).
 */
export class CollisionSystem extends CommonFilteredComponentSystem {
  public componentClasses: (keyof CommonRegistry)[] = ['Transform', 'Velocity', 'Collider'];
  protected simulationBound = true;

  /**
   * Override in subclasses to provide chunk data access.
   * Returns undefined if chunk data is not available (common systems can't access services).
   */
  protected getChunkDataProvider(): ChunkDataProvider | undefined {
    return undefined;
  }

  updateEntity({
    world,
    entityId,
    deltaTime,
  }: FilteredComponentSystemUpdateEntityArgs<CommonRegistry>): void {
    const transform = world.getComponent(entityId, 'Transform')!;
    const vel = world.getComponent(entityId, 'Velocity')!;
    const collider = world.getComponent(entityId, 'Collider')!;

    /**
     * CRITICAL: Skip server-side collision resolution for player-controlled entities.
     * The server validates collisions for players strictly upon receiving a
     * playerMove message.
     *
     * Exception: If a player is under an external force (Knockback), the server
     * must resolve collisions to ensure they don't get pushed into walls.
     */
    if (
      (!this.services.isClient &&
        world.hasComponent(entityId, 'PlayerControlled') &&
        !world.hasComponent(entityId, 'Knockback')) ||
      world.hasComponent(entityId, 'FastMovement') ||
      world.hasComponent(entityId, 'InBed')
    ) {
      return;
    }

    // Check collision at current position (already updated by MovementSystem)
    const entityRect: Rect = {
      x: transform.x + collider.offsetX,
      y: transform.y + collider.offsetY,
      width: collider.width,
      height: collider.height,
    };

    const provider = this.getChunkDataProvider();
    if (!provider) {
      return;
    }

    const result = checkMapCollision(provider, entityRect, transform.sceneId);
    const mtv = result?.mtv;

    if (result && mtv) {
      // Apply MTV to position to resolve overlap
      // Add a small epsilon to ensure we are definitely out of the collision
      const EPSILON = 0.001;
      let finalNewX = transform.x + mtv.x + (mtv.x !== 0 ? Math.sign(mtv.x) : 0) * EPSILON;
      let finalNewY = transform.y + mtv.y + (mtv.y !== 0 ? Math.sign(mtv.y) : 0) * EPSILON;

      // Zero out velocity components that oppose the MTV
      let newDx = vel.dx;
      let newDy = vel.dy;

      if ((mtv.x > 0 && vel.dx < 0) || (mtv.x < 0 && vel.dx > 0)) {
        newDx = 0;
      }
      if ((mtv.y > 0 && vel.dy < 0) || (mtv.y < 0 && vel.dy > 0)) {
        newDy = 0;
      }

      // CORNER CORRECTION ("Corner Sliding")
      // If movement is mostly cardinal, check if we should nudge the entity around a corner.
      const correction = calculateCornerCorrection({
        provider,
        sceneId: transform.sceneId,
        entityRect,
        collidingTiles: result.collidingTiles,
        mtv,
        vel,
        entitySpeed: this.services.speed.getEffectiveSpeed(world, entityId),
        deltaTime,
      });

      if (correction) {
        finalNewX += correction.x;
        finalNewY += correction.y;
      }

      this.updateComponent(entityId, transform, { x: finalNewX, y: finalNewY });

      if (newDx !== vel.dx || newDy !== vel.dy) {
        this.updateComponent(entityId, vel, { dx: newDx, dy: newDy });
      }
    }
  }
}
